import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as deliveryNoteService from '../services/deliveryNoteService';
import { generateAndPreviewDeliveryNotePDF } from '../services/deliveryNotePdfService';
import { useStoreInfo } from '../../../hooks/useCompanyInfo';
import {
  Box, Button, Card, CardContent, Typography, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Divider, Chip, Container, Alert, AlertTitle,
  CircularProgress, Backdrop, Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const DeliveryNoteViewPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { storeInfo } = useStoreInfo();

  const [deliveryNote, setDeliveryNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await deliveryNoteService.getDeliveryNoteById(id);
        const data = response?.payload || response?.data || response;
        setDeliveryNote(data);
      } catch (error) {
        console.error('Error fetching delivery note:', error);
        setDeliveryNote(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount || 0);

  const handleEdit = () => navigate(`/sales/delivery-notes/edit/${id}`);

  const handleDelete = async () => {
    if (window.confirm(t('confirm_delete_delivery_note') || 'Are you sure you want to delete this delivery note?')) {
      try {
        await deliveryNoteService.deleteDeliveryNote(id);
        toast.success(t('delivery_note_deleted') || 'Delivery note deleted successfully');
        navigate('/sales/delivery-notes');
      } catch (error) {
        console.error('Error deleting delivery note:', error);
        toast.error(t('failed_to_delete_delivery_note') || 'Failed to delete delivery note');
      }
    }
  };

  const handlePreviewPDF = async () => {
    setPdfLoading(true);
    try {
      await generateAndPreviewDeliveryNotePDF(deliveryNote, storeInfo, t);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(t('failed_to_generate_pdf') || 'Failed to generate PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const map = { Delivered: 'success', Pending: 'warning', Cancelled: 'error', Draft: 'default' };
    return map[status] || 'default';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!deliveryNote) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          <AlertTitle>Not Found</AlertTitle>
          {t('delivery_note_not_found') || 'Delivery note not found'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/sales/delivery-notes')} sx={{ mt: 2 }}>
          {t('back') || 'Back to Delivery Notes'}
        </Button>
      </Container>
    );
  }

  // Extract data
  const customer = deliveryNote.Customer || {};
  const customerName = customer.CompanyName || customer.Name || deliveryNote.CustomerName || 'N/A';
  const customerVAT = customer.VATNumber || deliveryNote.CustomerVAT || 'N/A';
  const customerEmail = customer.Email || deliveryNote.CustomerEmail || 'N/A';
  const customerPhone = customer.PhoneNumber || customer.MobilePhone || deliveryNote.CustomerPhone || 'N/A';
  const customerAddress = customer.Street || deliveryNote.CustomerAddress || 'N/A';
  const customerPostCode = customer.PostCode || '';
  const customerCity = customer.City || deliveryNote.CustomerCity || '';
  const customerCountry = customer.Country || '';

  const lineItems = deliveryNote.LineItems || [];
  const vatPercentage = Number(deliveryNote.VATPercentage || 0);
  const totalExcl = Number(deliveryNote.TotalExcl || deliveryNote.SubTotal || deliveryNote.Subtotal || 0);
  const totalVAT = Number(deliveryNote.TotalVAT || deliveryNote.TotalVATAmount || deliveryNote.VATAmount || deliveryNote.VatAmount || 0);
  const totalValue = Number(deliveryNote.TotalValue || deliveryNote.TotalAmount || deliveryNote.GrandTotal || 0);
  const totalItems = Number(deliveryNote.TotalItems || lineItems.length || 0);
  const totalQuantity = Number(deliveryNote.TotalQuantity || 0);
  const deliveryDate = deliveryNote.IssueDate || deliveryNote.DeliveryNoteDate || deliveryNote.CreatedDate;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* PDF Generation Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000, backgroundColor: 'rgba(0,0,0,0.7)' }}
        open={pdfLoading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" size={60} thickness={4} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('generating_pdf') || 'Generating PDF...'}
          </Typography>
        </Box>
      </Backdrop>

      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LocalShippingIcon sx={{ fontSize: 40, color: 'white' }} />
            <Box>
              <Typography variant="h4" fontWeight="700" sx={{ color: 'white' }}>
                {t('delivery_note') || 'Delivery Note'} #{deliveryNote.DeliveryNoteNumber}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.5 }}>
                {t('date') || 'Date'}: {formatDate(deliveryDate)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/sales/delivery-notes')}
              variant="contained"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', borderRadius: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
            >
              {t('back') || 'Back'}
            </Button>
            <Button
              variant="contained"
              startIcon={<PictureAsPdfIcon />}
              onClick={handlePreviewPDF}
              sx={{ bgcolor: '#d32f2f', color: 'white', borderRadius: 2, '&:hover': { bgcolor: '#b71c1c' } }}
            >
              {t('preview_pdf') || 'Preview PDF'}
            </Button>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEdit}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', borderRadius: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
            >
              {t('edit') || 'Edit'}
            </Button>
            <Button
              variant="contained"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              sx={{ bgcolor: 'rgba(211,47,47,0.7)', color: 'white', borderRadius: 2, '&:hover': { bgcolor: 'rgba(211,47,47,0.9)' } }}
            >
              {t('delete') || 'Delete'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Company Info */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BusinessIcon sx={{ color: '#667eea', mr: 1 }} />
              <Typography variant="h6" fontWeight="700" sx={{ color: '#667eea' }}>
                {t('company_information') || 'Company Information'}
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('name') || 'Name'}</Typography>
                <Typography variant="body1" fontWeight={500}>{storeInfo?.name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('vat_number') || 'VAT Number'}</Typography>
                <Typography variant="body1">{storeInfo?.vat || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('email') || 'Email'}</Typography>
                <Typography variant="body1">{storeInfo?.email || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('phone') || 'Phone'}</Typography>
                <Typography variant="body1">{storeInfo?.phone || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('address') || 'Address'}</Typography>
                <Typography variant="body1">
                  {storeInfo?.address || 'N/A'}
                  {storeInfo?.postCode && `, ${storeInfo.postCode}`}
                  {storeInfo?.city && ` ${storeInfo.city}`}
                  {storeInfo?.country && `, ${storeInfo.country}`}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('iban') || 'IBAN'}</Typography>
                <Typography variant="body1">{storeInfo?.iban || 'N/A'}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Customer Info */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PersonIcon sx={{ color: '#667eea', mr: 1 }} />
              <Typography variant="h6" fontWeight="700" sx={{ color: '#667eea' }}>
                {t('customer_information') || 'Customer Information'}
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('name') || 'Name'}</Typography>
                <Typography variant="body1" fontWeight={500}>{customerName}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('vat_number') || 'VAT Number'}</Typography>
                <Typography variant="body1">{customerVAT}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('email') || 'Email'}</Typography>
                <Typography variant="body1">{customerEmail}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('phone') || 'Phone'}</Typography>
                <Typography variant="body1">{customerPhone}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('address') || 'Address'}</Typography>
                <Typography variant="body1">
                  {customerAddress}
                  {customerPostCode && `, ${customerPostCode}`}
                  {customerCity && ` ${customerCity}`}
                  {customerCountry && `, ${customerCountry}`}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Delivery Note Summary */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', height: '100%' }}>
            <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
              {t('delivery_note_summary') || 'Delivery Note Summary'}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('delivery_note_number') || 'Delivery Note #'}</Typography>
                <Typography variant="body1" fontWeight={500}>{deliveryNote.DeliveryNoteNumber}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('status') || 'Status'}</Typography>
                <Chip
                  label={deliveryNote.Status || 'Pending'}
                  color={getStatusColor(deliveryNote.Status)}
                  size="small"
                  sx={{ mt: 0.5, fontWeight: 600 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('issue_date') || 'Issue Date'}</Typography>
                <Typography variant="body1">{formatDate(deliveryDate)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">{t('total_items') || 'Total Items'}</Typography>
                <Typography variant="body1" fontWeight={500}>{totalItems}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">{t('total_quantity') || 'Total Qty'}</Typography>
                <Typography variant="body1" fontWeight={500}>{totalQuantity}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              {vatPercentage > 0 && (
                <>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">{t('subtotal_excl_vat') || 'Subtotal (Excl. VAT)'}</Typography>
                    <Typography variant="h6" fontWeight={600}>{formatCurrency(totalExcl)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">{t('vat') || 'VAT'} ({vatPercentage}%)</Typography>
                    <Typography variant="h6" fontWeight={600}>{formatCurrency(totalVAT)}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('total') || 'Total'}</Typography>
                <Typography variant="h4" fontWeight={700} sx={{ color: '#667eea' }}>
                  {formatCurrency(totalValue)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Line Items */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
              {t('line_items') || 'Line Items'}
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)' }}>
                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t('product') || 'Product'}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">{t('quantity') || 'Quantity'}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t('unit_price') || 'Unit Price'}</TableCell>
                    {vatPercentage > 0 && (
                      <TableCell sx={{ fontWeight: 600 }} align="center">{t('vat') || 'VAT %'}</TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t('total_excl') || 'Total (Excl.)'}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t('total_incl') || 'Total (Incl.)'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.length > 0 ? (
                    lineItems.map((item, index) => {
                      const unitPrice = Number(item.UnitPrice || item.Price || 0);
                      const qty = Number(item.Quantity || 0);
                      const itemTotalExcl = Number(item.TotalExcl || item.SubTotal || (unitPrice * qty));
                      const vatAmt = Number(item.VATAmount || item.VatAmount || item.TaxAmount || item.TotalVAT || 0);
                      const itemTotalIncl = Number(item.TotalIncl || item.TotalInclVAT || item.Total || (itemTotalExcl + vatAmt));
                      return (
                        <TableRow key={index} hover>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <Typography fontWeight={500}>
                              {item.DisplayName || item.ProductName || item.Name || 'N/A'}
                            </Typography>
                            {item.Description && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {item.Description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={qty} size="small" color="info" sx={{ fontWeight: 600 }} />
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={500}>{formatCurrency(unitPrice)}</Typography>
                          </TableCell>
                          {vatPercentage > 0 && (
                            <TableCell align="center">
                              <Chip label={`${vatPercentage}%`} size="small" variant="outlined" color="primary" />
                            </TableCell>
                          )}
                          <TableCell align="right">{formatCurrency(itemTotalExcl)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: '#667eea' }}>
                            {formatCurrency(itemTotalIncl)}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ color: '#888', py: 4 }}>
                        {t('no_line_items') || 'No line items found'}
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Totals rows */}
                  {vatPercentage > 0 && (
                    <>
                      <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.05)' }}>
                        <TableCell colSpan={vatPercentage > 0 ? 6 : 5} align="right" sx={{ fontWeight: 700 }}>
                          {t('subtotal_excl_vat') || 'Subtotal (Excl. VAT)'}:
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#667eea' }}>
                          {formatCurrency(totalExcl)}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.05)' }}>
                        <TableCell colSpan={vatPercentage > 0 ? 6 : 5} align="right" sx={{ fontWeight: 700 }}>
                          {t('vat') || 'VAT'} ({vatPercentage}%):
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#667eea' }}>
                          {formatCurrency(totalVAT)}
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.15)' }}>
                    <TableCell colSpan={vatPercentage > 0 ? 6 : 5} align="right" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                      {t('total') || 'Total'}:
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#667eea' }}>
                      {formatCurrency(totalValue)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Notes */}
        {(deliveryNote.Notes || deliveryNote.CustomText) && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
                {t('notes') || 'Notes'}
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.8, color: '#555' }}>
                {deliveryNote.Notes || deliveryNote.CustomText}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default DeliveryNoteViewPage;
